import { Box, Flex, Grid, Spinner, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import { FormikCheckbox } from 'components/Formik';
import Loader from 'components/Loader';
import { routePaths } from 'global/routePaths';
import { useDisconnectQuickbook } from 'hooks/integrations/useDisconnectQuickbook';
import { useFetchQuickbookOnlineSettings } from 'hooks/integrations/useFetchQuickbookOnlineSettings';
import { useSaveQuickbookOnlineSetting } from 'hooks/integrations/useSaveQuickbookOnlineSettings';
import { useCompanyInfo } from 'store/useUserStore';

import { initialQuickbookOnlineSettings } from './schema';

interface IQBDSetting {
  onDisconnect?: () => void;
}

const QuickbookOnlineSettings: React.FC<IQBDSetting> = ({ onDisconnect }: IQBDSetting) => {
  const [formData, setFormData] = useState<any>(initialQuickbookOnlineSettings);

  const navigate = useNavigate();
  const companyInfo = useCompanyInfo();

  const { t } = useTranslation(['integrations', 'common']);

  const { status: qbDisconnectStatus, mutateAsync: qbDisconnectMutateAsync } = useDisconnectQuickbook();
  const { status: saveQuickbookSettingsStatus, mutateAsync: saveQuickbookSettingsMutateAsync } =
    useSaveQuickbookOnlineSetting();
  const { isFetching: isFetchingSettings, refetch } = useFetchQuickbookOnlineSettings({
    onSettled: (data: any) => {
      if (data) {
        const settingData = data?.payload;

        const formData = {
          should_import_customers: settingData?.allows_import_customers ? ['1'] : [],
          should_import_sub_customers: settingData?.allows_sub_customers ? ['1'] : [],
          should_import_projects: settingData?.allows_import_projects ? ['1'] : [],
          billable_status: settingData?.billable_status ? ['1'] : [],
          create_bills_from_timesheets: settingData?.create_bills_from_timesheets ? ['1'] : [],
        };
        setFormData(formData);
      }
    },
  });

  const onDisconnectQBD = () => {
    qbDisconnectMutateAsync().then(() => {
      if (onDisconnect) {
        onDisconnect();
      }
    });
  };

  const onConfigureMapping = () => {
    navigate(routePaths.integrations.quickbook.mapping);
  };

  const handleFormSubmit = async (values: typeof initialQuickbookOnlineSettings, { resetForm }: any) => {
    const postData = {
      should_import_customers: values.should_import_customers.length ? true : false,
      should_import_sub_customers: values.should_import_sub_customers.length ? true : false,
      should_import_projects: values.should_import_projects.length ? true : false,
      billable_status: values.billable_status.length ? true : false,
      create_bills_from_timesheets: values.create_bills_from_timesheets.length ? true : false,
    };

    saveQuickbookSettingsMutateAsync(postData).then(() => {
      resetForm();
      refetch();
    });
  };

  return (
    <Box>
      <Grid templateColumns="repeat(2, 1fr)" gap={6}>
        <Flex flexDirection="column">
          <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px" mb={6}>
            <Text fontSize="md" mb={4}>
              {t('integrations:quickbooks.companyFile')}
            </Text>

            <Text fontSize="sm" color="gray.500" mb={4}>
              {companyInfo?.name}
            </Text>

            <Text mb={4} color="primary.300" cursor="pointer" onClick={onConfigureMapping}>
              {t('integrations:quickbooks.configureMappings')}
            </Text>

            <Text color="primary.300" onClick={onDisconnectQBD} cursor="pointer">
              {t('integrations:quickbooks.disconnect')} {qbDisconnectStatus === 'loading' && <Spinner size="xs" />}
            </Text>
          </Box>
        </Flex>
        <Box>
          <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px">
            <Text fontSize="md" mb={4}>
              {t('integrations:quickbooks.settings')}
            </Text>
            {isFetchingSettings ? (
              <Loader height="150px" />
            ) : (
              <Formik initialValues={formData} enableReinitialize onSubmit={handleFormSubmit}>
                {() => (
                  <Form noValidate>
                    <FormikCheckbox
                      options={[{ label: t('integrations:quickbooks.importCustomers'), value: '1' }]}
                      name="should_import_customers"
                    />
                    <FormikCheckbox
                      options={[{ label: t('integrations:quickbooks.importSubCustomers'), value: '1' }]}
                      name="should_import_sub_customers"
                    />
                    <FormikCheckbox
                      options={[{ label: t('integrations:quickbooks.importProjects'), value: '1' }]}
                      name="should_import_projects"
                    />
                    <FormikCheckbox
                      options={[{ label: t('integrations:quickbooks.markTimeEntriesBillable'), value: '1' }]}
                      name="billable_status"
                    />
                    <FormikCheckbox
                      options={[
                        {
                          label: t('integrations:quickbooks.createBillsInQuickBooksOnlineFromTimesheetCosts'),
                          value: '1',
                        },
                      ]}
                      name="create_bills_from_timesheets"
                    />
                    <Button
                      type="submit"
                      colorScheme="secondary"
                      isLoading={saveQuickbookSettingsStatus === 'loading'}
                      minWidth="100px"
                    >
                      {t('common:save')}
                    </Button>
                  </Form>
                )}
              </Formik>
            )}
          </Box>
        </Box>
      </Grid>
    </Box>
  );
};
export default QuickbookOnlineSettings;
